import type { LocalizedText, Manufacturer } from "../schema/common.js";
import { MANUFACTURER_IDS } from "./manufacturers.js";
import { unitById } from "./units.js";

/**
 * Fight-o-Tron (D79): sixteen machine teams meet every evening in a knockout, one match every five minutes.
 * Nobody owns these squads and nothing is at stake for them — they exist so a player has something to watch
 * and something to bet on while their own frames are in the repair bay.
 */
export interface FightotronTeam {
  id: string;
  name: LocalizedText;
  /** Every frame of the team fights at this level; it is the team's whole difficulty dial. */
  level: number;
  commander: string;
  front: [string, string, string, string];
  support: [string, string];
}

function team(id: string, de: string, en: string, level: number, commander: string, front: [string, string, string, string], support: [string, string]): FightotronTeam {
  return { id, name: { de, en }, level, commander, front, support };
}

export const FIGHTOTRON: readonly FightotronTeam[] = [
  team("fot.ironyard", "Eisenhof", "Ironyard", 48, "unit.aegis_prime",
    ["unit.bulwark_titan", "unit.rampart_7", "unit.shieldwall_mk2", "unit.hellion_vanguard"], ["unit.seraph_unit", "unit.medic_prime"]),
  team("fot.nightshift", "Nachtschicht", "Night Shift", 46, "unit.overwatch_nyx",
    ["unit.nightshade", "unit.widow_zero", "unit.kitsune_9", "unit.photon_stalker"], ["unit.halo_mother", "unit.aurora_node"]),
  team("fot.redline", "Redline-Crew", "Redline Crew", 44, "unit.ironclad_boone",
    ["unit.solaris_lance", "unit.ryujin_blade", "unit.striker_beta", "unit.scrap_bastion"], ["unit.patch_bot", "unit.battery_mule"]),
  team("fot.kurogane", "Kurogane-Schule", "Kurogane School", 43, "unit.warden_kastor",
    ["unit.kagemori_ward", "unit.tessen_guard", "unit.ryujin_blade", "unit.mirage"], ["unit.relay_kite", "unit.field_nurse"]),
  team("fot.hollow", "Hohlgänger", "Hollow Walkers", 41, "unit.sable_marrow",
    ["unit.ember_wraith", "unit.basalt_golem", "unit.argent_tripod", "unit.glitch"], ["unit.aurora_node", "unit.toolbox"]),
  team("fot.saltworks", "Salzwerk", "Saltworks", 39, "unit.halcyon_vex",
    ["unit.rampart_7", "unit.barricade", "unit.grunt_cannon", "unit.spark_plug"], ["unit.medic_prime", "unit.battery_mule"]),
  team("fot.beacon", "Leuchtfeuer", "Beacon", 38, "unit.aegis_prime",
    ["unit.shieldwall_mk2", "unit.striker_beta", "unit.fang", "unit.firecracker"], ["unit.seraph_unit", "unit.patch_bot"]),
  team("fot.dustline", "Staublinie", "Dustline", 36, "unit.warden_kastor",
    ["unit.tessen_guard", "unit.argent_tripod", "unit.mirage", "unit.barricade"], ["unit.relay_kite", "unit.field_nurse"]),
  team("fot.coilworks", "Spulenwerk", "Coilworks", 34, "unit.halcyon_vex",
    ["unit.basalt_golem", "unit.photon_stalker", "unit.spark_plug", "unit.scrap_bastion"], ["unit.aurora_node", "unit.battery_mule"]),
  team("fot.ashmarket", "Aschenmarkt", "Ash Market", 32, "unit.sable_marrow",
    ["unit.kitsune_9", "unit.grunt_cannon", "unit.glitch", "unit.barricade"], ["unit.patch_bot", "unit.toolbox"]),
  team("fot.greenhouse", "Gewächshaus", "Greenhouse", 30, "unit.ironclad_boone",
    ["unit.shieldwall_mk2", "unit.fang", "unit.firecracker", "unit.scrap_bastion"], ["unit.field_nurse", "unit.relay_kite"]),
  team("fot.sandbar", "Sandbank", "Sandbar", 28, "unit.sgt_rook",
    ["unit.barricade", "unit.striker_beta", "unit.spark_plug", "unit.grunt_cannon"], ["unit.battery_mule", "unit.toolbox"]),
  team("fot.tinmen", "Blechmänner", "Tin Men", 25, "unit.cipher_odd",
    ["unit.scrap_bastion", "unit.fang", "unit.glitch", "unit.pillbox"], ["unit.field_nurse", "unit.toolbox"]),
  team("fot.lastcall", "Letzte Runde", "Last Call", 22, "unit.sgt_rook",
    ["unit.pillbox", "unit.grunt_cannon", "unit.firecracker", "unit.barricade"], ["unit.battery_mule", "unit.toolbox"]),
  team("fot.scrapheap", "Schrotthaufen", "Scrapheap", 18, "unit.cipher_odd",
    ["unit.pillbox", "unit.rustbucket", "unit.nailgun", "unit.fang"], ["unit.toolbox", "unit.field_nurse"]),
  team("fot.rookies", "Rekruten", "Rookies", 14, "unit.cadet_01",
    ["unit.pillbox", "unit.rustbucket", "unit.nailgun", "unit.grunt_cannon"], ["unit.toolbox", "unit.battery_mule"]),
];

const byId = new Map(FIGHTOTRON.map((t) => [t.id, t]));

export function fightotronTeamById(id: string): FightotronTeam {
  const found = byId.get(id);
  if (!found) throw new Error(`unknown Fight-o-Tron team ${id}`);
  return found;
}

/** The evening card opens at 18:00 server time and runs one match every five minutes. */
export const FIGHTOTRON_START_HOUR = 18;
export const FIGHTOTRON_MATCH_MS = 5 * 60_000;
/** Sixteen teams: eight matches, then four, then two, then the final — fifteen in all. */
export const FIGHTOTRON_MATCHES = FIGHTOTRON.length - 1;
/** Betting closes this long before a match starts, so nobody bets on a fight already being fought. */
export const FIGHTOTRON_BET_CLOSES_MS = 30_000;
/** The house keeps this share of the pool, which is what keeps the odds under a straight doubling. */
export const FIGHTOTRON_MARGIN = 0.08;
/** Credits a player may stake on one match (100 since D111: the pots are the players' money, not the house's). */
export const FIGHTOTRON_STAKE = { min: 10, max: 100 } as const;

/**
 * The pot (D111). Every stake is split three ways: half goes to the pot of the match it is on, a fifth to the
 * quarter-final pot, the rest to the final pot. Each pot is shared by stake among the tickets that backed the
 * winner; a pot nobody won rolls forward into the final pot, and a final pot nobody won carries into the next
 * card. Everything is paid at the end of the card, by mail.
 */
export const FIGHTOTRON_POT = { match: 0.5, quarter: 0.2, final: 0.3 } as const;
/** Later rounds are for those who were in from the start: a ticket in the opening round qualifies. */
export const FIGHTOTRON_QUALIFYING_ROUND = 1;
/** The underdog of a pairing draws one of these when the pairing is known; it is on the card for everyone to see. */
export const FIGHTOTRON_UNDERDOG_BUFFS = ["atk", "def", "hp"] as const;
export type FightotronBuff = (typeof FIGHTOTRON_UNDERDOG_BUFFS)[number];
export const FIGHTOTRON_UNDERDOG_PCT = 10;
/**
 * Sponsors (D111): every team is sponsored by the manufacturer most of its frames come from. On the floor the
 * sponsor buffs the team's frames of its own make; the champion's sponsor buffs every player's frames of that
 * make until the next card starts.
 */
export const FIGHTOTRON_SPONSOR_PCT = 10;
export function fightotronSponsor(team: FightotronTeam): Manufacturer {
  const count = new Map<Manufacturer, number>();
  for (const id of [team.commander, ...team.front, ...team.support]) {
    const mfr = unitById(id).mfr;
    count.set(mfr, (count.get(mfr) ?? 0) + 1);
  }
  let best: Manufacturer = MANUFACTURER_IDS[0]!;
  let bestN = -1;
  for (const mfr of MANUFACTURER_IDS) {
    const n = count.get(mfr) ?? 0;
    if (n > bestN) { best = mfr; bestN = n; }
  }
  return best;
}
/** Bots keep the pots alive with small tickets; they never take a share out (D111). */
export const FIGHTOTRON_BOT_STAKE = { min: 10, max: 30 } as const;
